import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Form, { List } from "rc-field-form";
import Modal from "../../../../../../components/Modal";
import { useVisible } from "../../../../../../utils/hooks/useVisible";
import { useGroups } from "../../../../../Settings/General/OrganisationLevelGroups/context";
import { read, utils } from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleInfo, faClose, faMinus } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "reactstrap";
import Field from "../../../../../../components/Field";
import Select from "../../../../../../components/Inputs/Select";
import useApi from "../../../../../../utils/api";
import useSWRMutation from "swr/mutation";
import UploadInput from "../../../../../../components/Inputs/Upload";
import Button from "../../../../../../components/Button";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";

const fileTypes = [
    "xlsx",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const fileSizeLimit = 2;

const fileToWorkbook = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            resolve(read(reader.result, { sheetRows: 11 })); // Read only first 10 rows (header + 10 data rows)
        };
        reader.onerror = () => {
            // could not parse the file
            reject();
        };
        reader.readAsArrayBuffer(file);
    });
}

const useLoadWorkSheet = (file) => {
    const [isLoading, setIsLoading] = useState(false);
    const [workSheet, setWorkSheet] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!file) {
            setWorkSheet(null);
            return;
        }
        (async () => {
            try {
                const workBook = await fileToWorkbook(file)
                setWorkSheet(workBook.Sheets[workBook.SheetNames[0]])
            } catch (err) {
                setError(err);
            }
        })();
    }, [file, setIsLoading]);

    return { isLoading, error, workSheet };
}

function ColumnSelect({ columns, info, ...props }) {
    const { visible, toggle } = useVisible();
    const ref = useRef();

    let allowClear = true;
    if (props.rules && Array.isArray(props.rules)) {
        allowClear = !props.rules.some((rule) => rule.required);
    }
    if (info && props.label) {
        props.label = (
            <div>
                <span className="mr-1">{props.label}</span>

                <span className="cursor-pointer">
                    <FontAwesomeIcon ref={ref} icon={faCircleInfo} />
                </span>

                <Tooltip target={ref} isOpen={visible} toggle={toggle}>
                    {info}
                </Tooltip>
            </div>
        );
    }

    return (
        <Field {...props}>
            {({ value, onChange }) => {
                return (
                    <Select
                        value={value}
                        onChange={onChange}
                        showSearch
                        allowClear={allowClear}
                    >
                        {value && (
                            <Select.Option key={value} value={value}>
                                {value}
                            </Select.Option>
                        )}

                        {columns.map((column, index) => {
                            return (
                                <Select.Option key={[column, index].join()} value={column}>
                                    {column}
                                </Select.Option>
                            );
                        })}
                    </Select>
                );
            }}
        </Field>
    );
}

function ImportModal({ visible, close, onSuccess, onFailure }) {
    const { t } = useTranslation();

    const [form] = Form.useForm();
    const [file, setFile] = useState(null);
    const { workSheet } = useLoadWorkSheet(file);

    const onChange = useCallback(({ target }) => {
        setFile(target.files[0]);
    }, []);

    const formValues = Form.useWatch([], form);
    const columns = useMemo(() => {
        if (!workSheet) {
            return [];
        }

        return utils
            .sheet_to_csv(workSheet)
            .split("\n")[0]
            .split(",")
            .filter((item) => {
                return !Object.values(formValues).includes(item);
            });
    }, [workSheet, formValues]);

    const { allocatedGroups } = useGroups();

    const { call } = useApi();
    const {
        data: response,
        error: mutationError,
        isMutating,
        trigger,
    } = useSWRMutation(
        "/schedules/import",
        async (url, { arg }) => {
            const data = new FormData();
            for (const key in arg) {
                const value = arg[key];
                if (value) {
                    data.append(key, value);
                }
            }
            if (Array.isArray(arg.breaks) && arg.breaks.length > 0) {
                data.set("breaks", JSON.stringify(arg.breaks));
            } else {
                data.delete("breaks");
            }
            data.append("file", file);
            return call({
                method: "POST",
                url,
                data,
            });
        },
        {
            onSuccess: onSuccess,
            onError: onFailure,
            throwOnError: false,
        },
    );

    return (
        <Modal
            className="payroll-export-modal"
            size="lg"
            title={
                <div className="d-flex justify-content-between align-items-center text-primary">
                    {t("schedule-import")}
                    <i
                        className="fa-solid fa-close cursor-pointer"
                        onClick={close}
                    />
                </div>
            }
            toggle={close}
            isOpen={visible}
            scrollable
            centered
        >
            {!response && (
                <>
                    {file ? (
                        <div
                            className="px-4 py-4 d-flex justify-content-between mb-4"
                            style={{
                                border: '2px solid rgba(0, 0, 0, 0.2)',
                                borderRadius: '.5rem',
                            }}
                        >
                            <span>
                                {file.name}
                            </span>

                            <FontAwesomeIcon
                                className="text-danger cursor-pointer"
                                icon={faClose}
                                onClick={() => {
                                    setFile(null);
                                    form.resetFields();
                                }}
                            />
                        </div>
                    ) : (
                        <UploadInput
                            multiple={false}
                            onChange={onChange}
                            fileTypes={fileTypes}
                            fileList={[]}
                            fileSizeLimit={fileSizeLimit}
                            insertText={t("upload-file")}
                            label={t("source")}
                            name="documents"
                            inputClassname="upload-input"
                        />
                    )}
                </>
            )}

            <div className={(file && !response) ? "d-block" : "d-none"}>
                <Form form={form} onFinish={trigger}>
                    <div className="d-flex gap-x-4">
                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="employeeNumber"
                            label={t("employee-number")}
                            rules={[
                                {
                                    required: true,
                                    message: t('required-employee-number'),
                                }
                            ]}
                        />

                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="job"
                            label={t("job")}
                        />

                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="location"
                            label={t("location")}
                        />
                    </div>

                    <div className="d-flex gap-x-4">
                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="date"
                            label={t("date")}
                            rules={[
                                {
                                    required: true,
                                    message: t('required-date'),
                                }
                            ]}
                        />

                        {/* only units are not required to have this field */}
                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="startDate"
                            label={t("start-date")}
                            rules={[{
                                required: true,
                                message: t('required-start-date'),
                            }]}
                        />

                        {/* only units are not required to have this field */}
                        <ColumnSelect
                            className="flex-fill"
                            columns={columns}
                            name="endDate"
                            label={t("end-date")}
                            rules={[{
                                required: true,
                                message: t('required-end-date'),
                            }]}
                        />
                    </div>

                    {/**
                    <List name="breaks">
                        {(fields, { add, remove }) => {
                            return (
                                <div className="bg-lighter p-4 mb-4 rounded">
                                    <h3 className="mb-3">{t("breaks")}</h3>
                                    {fields.map((field, index) => {
                                        return (
                                            <div key={field.key} className="bg-light p-3 rounded mb-3">
                                                <h4>{t("break-{{count}}", { count: index + 1 })}</h4>
                                                <div className="d-flex align-items-center gap-4">
                                                    <ColumnSelect
                                                        className="mb-0 flex-fill"
                                                        columns={columns}
                                                        name={[field.name, "start"]}
                                                        label={t("start")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t('required-start'),
                                                            }
                                                        ]}
                                                    />
                                                    <ColumnSelect
                                                        className="mb-0 flex-fill"
                                                        columns={columns}
                                                        name={[field.name, "end"]}
                                                        label={t("end")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t('required-end'),
                                                            }
                                                        ]}
                                                    />
                                                    <FontAwesomeIcon
                                                        className="text-danger cursor-pointer"
                                                        icon={faMinus}
                                                        onClick={() => remove(field.name)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <Button onClick={() => add({})} className="w-100">
                                        {t("add-break")}
                                    </Button>
                                </div>
                            );
                        }}
                    </List>
                     */}

                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0rem 1rem",
                    }}>
                        {allocatedGroups.map((group) => {
                            return (
                                <ColumnSelect
                                    key={group.id}
                                    columns={columns}
                                    name={group.level.replace("_", "")}
                                    label={renderSettingsResourceLabel(group)}
                                />
                            );
                        })}
                    </div>

                    <Button type="submit" color="primary" className="w-100" loading={isMutating}>
                        {t("submit")}
                    </Button>
                </Form>
            </div>

            {response && (
                <div>
                    {response.runOnBackground ? (
                    <div>
                        <h3>
                            {t("import-in-progress-async")}
                        </h3>
                        <p>
                                {t("import-in-progress-async-description", {
                                    totalItems: response.totalItems,
                            })}
                        </p>
                    </div>
                    ) : Object.keys(response).map((employeeNumber) => {
                        const { status, message, ...rows } = response[employeeNumber];
                        const { inserted, failed } = Object
                            .entries(rows)
                            .reduce(
                                (total, [row, value]) => {
                                    if (value.status) {
                                        total.inserted[row] = value;
                                    } else {
                                        total.failed[row] = value;
                                    }
                                    return total;
                                },
                                { inserted: {}, failed: {} }
                            );

                        return (
                            <div
                                key={employeeNumber}
                                className="p-3 rounded mb-4"
                                style={{ backgroundColor: "#f7fafc" }}
                            >
                                <h3>
                                    <span className="mr-3">
                                        {employeeNumber === 'emptyUser' ? (
                                            t('other')
                                        ) : `${t("employee")} ${employeeNumber}`}
                                    </span>

                                    {status ? (
                                        <>
                                            {!!Object.keys(inserted).length && (
                                                <span className="text-success mr-3">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    {Object.keys(inserted).length}
                                                </span>
                                            )}

                                            {!!Object.keys(failed).length && (
                                                <span className="text-danger mr-3">
                                                    <FontAwesomeIcon icon={faClose} />
                                                    {Object.keys(failed).length}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-danger">{message}</span>
                                    )}
                                </h3>

                                {status && (
                                    <div>
                                        {!!Object.keys(failed).length && (
                                            <hr className="my-2" />
                                        )}

                                        {Object.keys(failed).map((row) => {
                                            return (
                                                <div key={row} className="text-danger">
                                                    <b>{t("row-{{count}}", { count: row })}:</b>
                                                    {" "}
                                                    <span>{failed[row].message}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="d-flex gap-x-4">
                        <Button color="danger" className="w-100" onClick={() => close()}>
                            {t("close")}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

export default ImportModal;
